import type { FunnelStep } from "../api";

type Props = {
  started: number;
  completed: number;
  steps: FunnelStep[];
  questionLabels: Record<string, string>;
};

type Bar = {
  key: string;
  label: string;
  value: number;
  drop_off_pct: number | null;
  emphasis: "anchor" | "step";
};

export function FunnelChart({ started, completed, steps, questionLabels }: Props) {
  const lastAnswered =
    steps.length > 0 ? steps[steps.length - 1]!.answered : 0;
  const completedDrop =
    lastAnswered > 0
      ? Math.max(0, Math.round(((lastAnswered - completed) / lastAnswered) * 10000) / 100)
      : null;

  const bars: Bar[] = [
    { key: "started", label: "Started", value: started, drop_off_pct: null, emphasis: "anchor" },
    ...steps.map((s): Bar => ({
      key: s.key,
      label: s.question_id ? questionLabels[s.question_id] ?? s.label : s.label,
      value: s.viewed,
      drop_off_pct: s.drop_off_pct,
      emphasis: "step",
    })),
    {
      key: "completed",
      label: "Completed",
      value: completed,
      drop_off_pct: completedDrop,
      emphasis: "anchor",
    },
  ];

  const max = Math.max(1, ...bars.map((b) => b.value));

  return (
    <div className="rounded-2xl border border-mars-line bg-mars-charcoal/40 p-6">
      <div className="space-y-4">
        {bars.map((b) => {
          const widthPct = Math.round((b.value / max) * 100);
          return (
            <div key={b.key} className="grid grid-cols-[1fr_auto] items-center gap-5">
              <div>
                <div className="mb-1.5 flex items-baseline justify-between gap-2">
                  <span
                    className={
                      b.emphasis === "anchor"
                        ? "mono text-[0.7rem] uppercase tracking-[0.16em] text-mars-orange"
                        : "mono truncate text-[0.7rem] uppercase tracking-[0.14em] text-mars-white/70"
                    }
                  >
                    {b.label}
                  </span>
                  {b.drop_off_pct !== null && b.drop_off_pct > 0 ? (
                    <span className="mono text-[0.65rem] uppercase tracking-[0.16em] text-red-400">
                      -{b.drop_off_pct}%
                    </span>
                  ) : null}
                </div>
                <div className="h-7 rounded bg-mars-line">
                  <div
                    className={
                      b.emphasis === "anchor"
                        ? "h-7 rounded bg-mars-orange"
                        : "h-7 rounded bg-mars-orange/70"
                    }
                    style={{ width: `${widthPct}%` }}
                    aria-label={`${b.label} ${b.value}`}
                  />
                </div>
              </div>
              <div className="mono text-base text-mars-white tabular-nums">{b.value}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
